import { useEffect, useState } from 'react';
import { ActivityIndicator, Alert, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import type { TripTemplate } from '@/types/tripTemplates';
import {
  isTemplateRecommendationSaved,
  saveTemplateRecommendation,
  unsaveRecommendation,
} from '@/services/recommendations';

type SaveTemplateButtonProps = {
  userId: string;
  template: TripTemplate;
  size?: number;
  onSaved?: (id: string) => void;
  onUnsaved?: () => void;
};

export function SaveTemplateButton({
  userId,
  template,
  size = 24,
  onSaved,
  onUnsaved,
}: SaveTemplateButtonProps) {
  const theme = useTheme();
  const [isSaved, setIsSaved] = useState(false);
  const [savedId, setSavedId] = useState<string | undefined>(undefined);
  const [checking, setChecking] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!userId || !template?.id) {
        setChecking(false);
        return;
      }
      setChecking(true);
      try {
        const res = await isTemplateRecommendationSaved(userId, template.id);
        if (!cancelled) {
          setIsSaved(res.isSaved);
          setSavedId(res.savedRecommendationId);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [userId, template?.id]);

  const handlePress = async () => {
    if (loading || checking) return;

    if (!userId) {
      Alert.alert('Sign in required', 'Please sign in to save templates.');
      return;
    }
    if (!template?.id) {
      Alert.alert('Error', 'Missing template id.');
      return;
    }

    setLoading(true);
    try {
      if (isSaved && savedId) {
        await unsaveRecommendation(userId, savedId);
        setIsSaved(false);
        setSavedId(undefined);
        onUnsaved?.();
      } else {
        const id = await saveTemplateRecommendation(userId, template);
        setIsSaved(true);
        setSavedId(id);
        onSaved?.(id);
      }
    } catch (error) {
      Alert.alert('Error', error instanceof Error ? error.message : 'Unable to save template. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <ActivityIndicator
        size="small"
        color={theme.colors.textSecondary}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <Pressable
      onPress={handlePress}
      disabled={loading}
      style={({ pressed }) => ({
        opacity: pressed || loading ? 0.6 : 1,
        padding: theme.spacing(0.5),
      })}
      accessibilityRole="button"
      accessibilityLabel={isSaved ? 'Unsave template' : 'Save template'}
    >
      {loading ? (
        <ActivityIndicator size="small" color={theme.colors.primary} />
      ) : (
        <Ionicons
          name={isSaved ? 'heart' : 'heart-outline'}
          size={size}
          color={isSaved ? theme.colors.primary : theme.colors.textSecondary}
        />
      )}
    </Pressable>
  );
}


